<template>
  <b-modal
    :id="modalId"
    :visible="visible"
    :title="title"
    :ok-title="okTitle"
    :cancel-title="cancelTitle"
    ok-variant="primary"
    cancel-variant="outline-secondary"
    size="lg"
    ok-only
    @ok="handleOk"
    @cancel="handleCancel"
    @hidden="handleCancel">
    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="primary"
        :disabled="!isFormValid"
        @click="ok()">
        {{ okTitle }}
      </b-button>
      <b-button
        variant="outline-secondary"
        @click="cancel()">
        {{ cancelTitle }}
      </b-button>
    </template>
    <p class="mb-3">{{ message }}</p>

    <!-- Tipo de Remarcação -->
    <b-form-group
      label="Tipo de Remarcação"
      :label-for="`${modalId}-schedule-type`">
      <b-form-radio-group
        :id="`${modalId}-schedule-type`"
        v-model="scheduleType"
        :options="scheduleTypeOptions"
        @change="onScheduleTypeChange"
        class="mb-3"></b-form-radio-group>
    </b-form-group>

    <!-- Seleção de Horário Disponível -->
    <div v-if="scheduleType === 'available'">
      <b-form-group
        label="Data"
        :label-for="`${modalId}-available-date`">
        <b-form-input
          :id="`${modalId}-available-date`"
          type="date"
          v-model="availableDate"
          :state="availableDateValidationState"
          @change="validateDateWithSchedule"></b-form-input>
        <b-form-invalid-feedback :state="availableDateValidationState">
          {{ availableDateError }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        label="Horário Disponível"
        :label-for="`${modalId}-available-schedule`">
        <b-form-select
          :id="`${modalId}-available-schedule`"
          v-model="selectedAvailableScheduleId"
          :options="availableScheduleOptions"
          :state="availableScheduleValidationState"
          @change="validateDateWithSchedule">
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Selecione um horário --
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="availableScheduleValidationState">
          Selecione um horário disponível
        </b-form-invalid-feedback>
        <small v-if="loadingSchedules" class="text-muted">
          <b-spinner small></b-spinner> Carregando horários disponíveis...
        </small>
        <small v-if="!loadingSchedules && availableSchedules.length === 0" class="text-danger">
          Nenhum horário disponível encontrado
        </small>
      </b-form-group>
    </div>

    <!-- Campos para Horário Livre -->
    <div v-if="scheduleType === 'free'">
      <b-form-group
        label="Nova Data"
        :label-for="`${modalId}-new-date`">
        <b-form-input
          :id="`${modalId}-new-date`"
          type="date"
          v-model="newDate"
          :state="newDateValidationState"></b-form-input>
        <b-form-invalid-feedback :state="newDateValidationState">
          A data é obrigatória
        </b-form-invalid-feedback>
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group
            label="Horário Início"
            :label-for="`${modalId}-start-time`">
            <b-form-input
              :id="`${modalId}-start-time`"
              type="time"
              v-model="startTime"
              :state="startTimeValidationState"></b-form-input>
            <b-form-invalid-feedback :state="startTimeValidationState">
              O horário de início é obrigatório
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Horário Fim"
            :label-for="`${modalId}-end-time`">
            <b-form-input
              :id="`${modalId}-end-time`"
              type="time"
              v-model="endTime"
              :state="endTimeValidationState"></b-form-input>
            <b-form-invalid-feedback :state="endTimeValidationState">
              O horário de fim é obrigatório
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Justificativa -->
    <b-form-group
      :label="justificationLabel"
      :label-for="`${modalId}-justification`">
      <b-form-textarea
        :id="`${modalId}-justification`"
        v-model="localJustification"
        rows="4"
        :placeholder="justificationPlaceholder"
        :state="validationState"></b-form-textarea>
      <b-form-invalid-feedback :state="validationState">
        {{ errorMessage }}
      </b-form-invalid-feedback>
    </b-form-group>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormGroup, BFormTextarea, BFormInvalidFeedback, BFormRadioGroup, BFormSelect, BFormSelectOption, BFormInput, BRow, BCol, BSpinner } from 'bootstrap-vue'
import LessonService from '@/services/api/LessonService'

export default {
  name: 'RescheduleModal',
  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormRadioGroup,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BRow,
    BCol,
    BSpinner
  },
  props: {
    modalId: {
      type: String,
      default: 'reschedule-modal'
    },
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      required: true
    },
    justificationLabel: {
      type: String,
      default: 'Justificativa'
    },
    justificationPlaceholder: {
      type: String,
      default: 'Digite o motivo da remarcação...'
    },
    okTitle: {
      type: String,
      default: 'Confirmar'
    },
    cancelTitle: {
      type: String,
      default: 'Cancelar'
    },
    errorMessage: {
      type: String,
      default: 'A justificativa é obrigatória'
    },
    lessonId: {
      type: Number,
      default: null
    },
    referenceId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      localJustification: '',
      validationState: null,
      scheduleType: 'free', // 'free' ou 'available'
      scheduleTypeOptions: [
        { text: 'Horário Livre', value: 'free' },
        { text: 'Horários Disponíveis', value: 'available' }
      ],
      selectedAvailableScheduleId: null,
      availableSchedules: [],
      loadingSchedules: false,
      availableScheduleValidationState: null,
      availableDate: '',
      availableDateValidationState: null,
      availableDateError: 'A data é obrigatória',
      newDate: '',
      newDateValidationState: null,
      startTime: '',
      startTimeValidationState: null,
      endTime: '',
      endTimeValidationState: null,
      lessonService: null
    }
  },
  computed: {
    availableScheduleOptions() {
      return this.availableSchedules.map(schedule => {
        const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
        const dayName = dayNames[schedule.day_of_week]
        return {
          value: schedule.id,
          text: `${dayName} - ${schedule.start_time} às ${schedule.end_time}`
        }
      })
    },
    isFormValid() {
      // Verifica se a justificativa está preenchida
      if (!this.localJustification || this.localJustification.trim() === '') {
        return false
      }

      // Valida campos específicos baseado no tipo de remarcação
      if (this.scheduleType === 'available') {
        // Precisa ter data e horário selecionados
        if (!this.availableDate || !this.selectedAvailableScheduleId) {
          return false
        }

        // Se a data e horário estão selecionados, precisa validar se correspondem
        if (this.availableDate && this.selectedAvailableScheduleId) {
          const selectedSchedule = this.availableSchedules.find(
            s => s.id === this.selectedAvailableScheduleId
          )

          if (selectedSchedule) {
            const selectedDate = new Date(this.availableDate + 'T00:00:00')
            const selectedDateDayOfWeek = selectedDate.getDay()
            const scheduleDayOfWeek = selectedSchedule.day_of_week

            // Se os dias não correspondem, o formulário não é válido
            if (selectedDateDayOfWeek !== scheduleDayOfWeek) {
              return false
            }
          }
        }
      } else if (this.scheduleType === 'free') {
        // Precisa ter data, horário início e fim
        if (!this.newDate || !this.startTime || !this.endTime) {
          return false
        }
      }

      // Se passou todas as validações, o formulário é válido
      return true
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        // Sempre carrega os horários disponíveis quando o modal abrir
        // Assim os dados estarão atualizados quando o usuário selecionar a opção
        if (this.referenceId) {
          this.loadAvailableSchedules()
        }
      }
    }
  },
  created() {
    this.lessonService = LessonService.build(this.$vs)
  },
  methods: {
    onScheduleTypeChange(value) {
      // Os horários já são carregados quando o modal abre
      // Apenas para garantir, recarrega se não tiver nenhum horário
      if (value === 'available' && this.referenceId && this.availableSchedules.length === 0) {
        console.log('⚠️ Recarregando horários disponíveis pois o array estava vazio')
        this.loadAvailableSchedules()
      }
    },
    validateDateWithSchedule() {
      // Se não tiver data ou horário selecionado, não valida ainda
      if (!this.availableDate || !this.selectedAvailableScheduleId) {
        return
      }

      // Encontra o horário selecionado
      const selectedSchedule = this.availableSchedules.find(
        s => s.id === this.selectedAvailableScheduleId
      )

      if (!selectedSchedule) {
        return
      }

      // Converte a data selecionada para Date
      const selectedDate = new Date(this.availableDate + 'T00:00:00')

      // getDay() do JS: 0=Domingo, 1=Segunda, 2=Terça, ..., 6=Sábado
      const selectedDateDayOfWeek = selectedDate.getDay()

      // day_of_week do schedule vem do backend como:
      // monday=1, tuesday=2, wednesday=3, thursday=4, friday=5, saturday=6, sunday=0
      const scheduleDayOfWeek = selectedSchedule.day_of_week

      // Mapeia o day_of_week do schedule para o formato do getDay() do JS
      // Backend: monday=1 -> JS: segunda=1 ✓
      // Backend: sunday=0 -> JS: domingo=0 ✓
      // Então o mapeamento é direto!
      const expectedDayOfWeek = scheduleDayOfWeek

      // Verifica se a data corresponde ao dia da semana do horário
      if (selectedDateDayOfWeek !== expectedDayOfWeek) {
        const dayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']
        const scheduleDayName = dayNames[expectedDayOfWeek]
        this.availableDateError = `A data selecionada deve ser uma ${scheduleDayName} para corresponder ao horário escolhido`
        this.availableDateValidationState = false
        console.warn(`❌ Data inválida: selecionado ${dayNames[selectedDateDayOfWeek]}, esperado ${scheduleDayName}`)
      } else {
        this.availableDateValidationState = true
        console.log('✅ Data válida para o horário selecionado')
      }
    },
    loadAvailableSchedules() {
      if (!this.referenceId) {
        console.warn('referenceId (lesson ID) não fornecido para carregar horários disponíveis')
        return
      }

      console.log('🔍 Carregando horários disponíveis para lesson ID:', this.referenceId)

      this.loadingSchedules = true
      // Usa o reference_id que é o ID da Lesson para buscar os horários disponíveis
      this.lessonService.getAllAvailableSchedules(this.referenceId).then(
        response => {
          console.log('📥 Response do getAllAvailableSchedules:', response)

          // Processa os dados para criar uma lista plana de horários
          const schedules = []
          if (Array.isArray(response)) {
            response.forEach((row, rowIndex) => {
              console.log('🔍 Processando row:', row)

              // Mapeia dias da semana para valores compatíveis com getDay() do JavaScript
              // getDay(): 0=Domingo, 1=Segunda, 2=Terça, 3=Quarta, 4=Quinta, 5=Sexta, 6=Sábado
              const daysMapping = [
                { key: 'monday', dayOfWeek: 1 },
                { key: 'tuesday', dayOfWeek: 2 },
                { key: 'wednesday', dayOfWeek: 3 },
                { key: 'thursday', dayOfWeek: 4 },
                { key: 'friday', dayOfWeek: 5 },
                { key: 'saturday', dayOfWeek: 6 },
                { key: 'sunday', dayOfWeek: 0 }
              ]

              daysMapping.forEach(({ key, dayOfWeek }) => {
                // Cada dia da semana tem seu próprio ID dentro dele
                if (row[key] && row[key].start_time && row[key].end_time && row[key].id) {
                  schedules.push({
                    id: `${row[key].id}_${key}`, // Usa o ID que está dentro do dia
                    day_of_week: dayOfWeek,
                    start_time: row[key].start_time,
                    end_time: row[key].end_time,
                    available_schedule_id: row[key].id // ID real do available_schedule
                  })
                  console.log(`   ✅ Adicionado: ${key} (ID: ${row[key].id})`)
                }
              })
            })
          }
          this.availableSchedules = schedules
          console.log('✅ Horários disponíveis carregados:', schedules.length, schedules)
        },
        error => {
          console.error('❌ Erro ao carregar horários disponíveis:', error)
          this.$vs.notify({
            title: 'Erro',
            text: 'Não foi possível carregar os horários disponíveis',
            color: 'danger',
            position: 'top-right'
          })
        }
      ).finally(() => {
        this.loadingSchedules = false
      })
    },
    handleOk(bvModalEvent) {
      // Previne o fechamento automático do modal
      bvModalEvent.preventDefault()

      // Valida a justificativa
      if (!this.localJustification || this.localJustification.trim() === '') {
        this.validationState = false
        return
      }
      this.validationState = true

      // Valida campos específicos baseado no tipo de remarcação
      let isValid = true

      if (this.scheduleType === 'available') {
        if (!this.availableDate) {
          this.availableDateValidationState = false
          this.availableDateError = 'A data é obrigatória'
          isValid = false
        } else {
          this.availableDateValidationState = true
        }

        if (!this.selectedAvailableScheduleId) {
          this.availableScheduleValidationState = false
          isValid = false
        } else {
          this.availableScheduleValidationState = true
        }

        // Valida se a data corresponde ao dia da semana do horário
        if (this.availableDate && this.selectedAvailableScheduleId) {
          this.validateDateWithSchedule()
          // Se a validação falhou, marca como inválido
          if (this.availableDateValidationState === false) {
            isValid = false
          }
        }
      } else if (this.scheduleType === 'free') {
        if (!this.newDate) {
          this.newDateValidationState = false
          isValid = false
        } else {
          this.newDateValidationState = true
        }

        if (!this.startTime) {
          this.startTimeValidationState = false
          isValid = false
        } else {
          this.startTimeValidationState = true
        }

        if (!this.endTime) {
          this.endTimeValidationState = false
          isValid = false
        } else {
          this.endTimeValidationState = true
        }
      }

      if (!isValid) {
        return
      }

      // Prepara os dados para emitir
      const data = {
        justification: this.localJustification.trim(),
        scheduleType: this.scheduleType
      }

      if (this.scheduleType === 'available') {
        // Encontra o schedule selecionado para pegar o ID real do available_schedule
        const selectedSchedule = this.availableSchedules.find(
          s => s.id === this.selectedAvailableScheduleId
        )

        if (selectedSchedule && selectedSchedule.available_schedule_id) {
          data.availableScheduleId = selectedSchedule.available_schedule_id
        } else {
          // Fallback: usa o ID composto se não encontrar o available_schedule_id
          data.availableScheduleId = this.selectedAvailableScheduleId
        }

        data.availableDate = this.availableDate

        console.log('📤 Enviando remarcação com horário disponível:', {
          availableScheduleId: data.availableScheduleId,
          availableDate: data.availableDate
        })
      } else if (this.scheduleType === 'free') {
        data.newDate = this.newDate
        data.startTime = this.startTime
        data.endTime = this.endTime

        console.log('📤 Enviando remarcação com horário livre:', {
          newDate: data.newDate,
          startTime: data.startTime,
          endTime: data.endTime
        })
      }

      this.$emit('confirm', data)

      // Fecha o modal
      this.$nextTick(() => {
        this.$bvModal.hide(this.modalId)
      })
    },
    handleCancel() {
      this.resetForm()
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    resetForm() {
      this.localJustification = ''
      this.validationState = null
      this.scheduleType = 'free'
      this.selectedAvailableScheduleId = null
      this.availableScheduleValidationState = null
      this.availableDate = ''
      this.availableDateValidationState = null
      this.availableDateError = 'A data é obrigatória'
      this.newDate = ''
      this.newDateValidationState = null
      this.startTime = ''
      this.startTimeValidationState = null
      this.endTime = ''
      this.endTimeValidationState = null
    }
  }
}
</script>

<style scoped>
/* Adicione estilos personalizados se necessário */
</style>
